// =============================================================================
// Moderation reason codes — Phase 3C taxonomy
//
// Canonical list shared between the App (`tenant-toggle-journal-visibility`
// edge fn validates against this set), the Platform UI (Stories hide modal
// dropdown), the Dashboard (trend chart legend), and the operator email
// template (renders the human label, not the code).
//
// The database CHECK constraint in
// `20260729_journal_moderation_taxonomy.sql` enforces the same list — if
// you add a code here you MUST also alter the CHECK constraint, or any
// hide attempt with the new code will 23514-error on insert.
//
// Stable ordering matches the dropdown order in the Hide modal. Higher
// up = more common in practice (operator tested guesses).
// =============================================================================

export type ModerationReasonCode =
  | 'spam'
  | 'off_topic'
  | 'language'
  | 'copyright'
  | 'pii'
  | 'requested_removal'
  | 'other';

export interface ModerationReasonMeta {
  /** The DB-stored code. Also the constant value. */
  code:     ModerationReasonCode;
  /** Short label rendered in the dropdown + chips. */
  label:    string;
  /** Tooltip / longer explanation shown under the label in the picker. */
  hint:     string;
  /** Tailwind text color class used for chip rendering in the trend chart.
   *  Picked so all 7 colors stay distinguishable in a stacked bar without
   *  visually colliding with the existing status chip palette
   *  (emerald = published, amber = hidden). */
  chipBg:   string;
  chipText: string;
}

export const MODERATION_REASONS: ModerationReasonMeta[] = [
  {
    code: 'spam',
    label: 'Spam or promotion',
    hint:  'Bot output, link farms, irrelevant promotional content.',
    chipBg:   'bg-rose-500/15',
    chipText: 'text-rose-700',
  },
  {
    code: 'off_topic',
    label: 'Off-topic',
    hint:  'Not travel-related, or breaks the tenant’s editorial theme.',
    chipBg:   'bg-amber-500/15',
    chipText: 'text-amber-700',
  },
  {
    code: 'language',
    label: 'Inappropriate language',
    hint:  'Profanity, slurs, or abusive content.',
    chipBg:   'bg-orange-500/15',
    chipText: 'text-orange-700',
  },
  {
    code: 'copyright',
    label: 'Copyright / re-post',
    hint:  'Re-posted from another source without attribution or permission.',
    chipBg:   'bg-purple-500/15',
    chipText: 'text-purple-700',
  },
  {
    code: 'pii',
    label: 'Exposes personal info',
    hint:  'Phone, address, license plate, or other PII visible in the story.',
    chipBg:   'bg-blue-500/15',
    chipText: 'text-blue-700',
  },
  {
    code: 'requested_removal',
    label: 'Removal requested',
    hint:  'Author, subject, or a third party requested takedown.',
    chipBg:   'bg-teal-500/15',
    chipText: 'text-teal-700',
  },
  {
    code: 'other',
    label: 'Other',
    hint:  'None of the above — please leave a note explaining why.',
    chipBg:   'bg-surface-200',
    chipText: 'text-surface-700',
  },
];

/** Lookup-by-code helper — used by chip rendering on the Stories table
 *  and the Dashboard trend card. Returns 'other' as fallback so unknown
 *  codes (e.g. from a future expansion) don't crash the UI. */
export function reasonMeta(code: string | null | undefined): ModerationReasonMeta {
  if (!code) return MODERATION_REASONS[MODERATION_REASONS.length - 1]; // 'other'
  const hit = MODERATION_REASONS.find(r => r.code === code);
  return hit ?? MODERATION_REASONS[MODERATION_REASONS.length - 1];
}

/** Just the codes, in display order — handy for arrays in select
 *  options or filter pill rows without having to .map repeatedly. */
export const MODERATION_REASON_CODES: ModerationReasonCode[] =
  MODERATION_REASONS.map(r => r.code);
